
import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import WebsiteLogo from "@/components/common/WebsiteLogo";
import { Button } from "@/components/ui/button";

interface RoleProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
  allowAll?: boolean; // For /welcome page
}

const RoleProtectedRoute = ({ children, requiredRoles, allowAll }: RoleProtectedRouteProps) => {
  const { currentUser, loading, userProfile } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !currentUser) {
      console.log("Unauthorized access attempt to:", location.pathname);
    }
  }, [currentUser, loading, location.pathname]);

  if (loading) {
    // Show nothing while auth state is being determined
    return null;
  }

  // Allow all users to access /welcome page if allowAll is set
  if (allowAll) {
    return <>{children}</>;
  }

  if (!currentUser || !userProfile) {
    return <FullPageError currentUser={currentUser} location={location} />;
  }

  // If requiredRoles is set, check if user has one of them
  if (requiredRoles && !requiredRoles.includes(userProfile.role)) {
    return <FullPageError currentUser={currentUser} location={location} />;
  }

  return <>{children}</>;
};

interface FullPageErrorProps {
  currentUser?: any;
  location?: any;
}

const FullPageError = ({ currentUser, location }: FullPageErrorProps) => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [showLoading, setShowLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("Setting up your account...");

  useEffect(() => {
    // Check if we're in an account creation flow
    const isAccountCreation = 
      currentUser && // User is authenticated
      !currentUser.isAnonymous && // Not anonymous
      (
        location?.state?.fromAccountCreation || // Explicit flag from navigation
        localStorage.getItem('kb_account_creation_flow') || // Account creation flag
        localStorage.getItem('kb_invite_token') || // Has invitation token
        location?.search?.includes('success=account-created') || // Coming from account creation
        document.referrer.includes('/create-account') // Came from create account page
      );

    if (isAccountCreation) {
      // Show loading for account setup
      setLoadingMessage("Setting up your account...");
      
      // After 5 seconds, if still loading, show a different message
      const timer1 = setTimeout(() => {
        setLoadingMessage("Almost ready... finalizing your account setup...");
      }, 5000);

      // After 10 seconds, show error
      const timer2 = setTimeout(() => {
        setShowLoading(false);
        // Clean up the account creation flag
        localStorage.removeItem('kb_account_creation_flow');
      }, 10000);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    } else {
      // Not account creation, show error immediately
      setShowLoading(false);
    }
  }, [currentUser, location]);

  const handleGoToLogin = async () => {
    try {
      // First, sign out the user to clear their authentication state.
      // KB-3270: `{ federated: false }` — this fires when someone WITHOUT pro
      // access is ejected to the login page. They are being refused pro, not
      // logging out, so we must NOT clear their apex (kidsbooked.com) cookie:
      // that would eject them from a consumer site they are entitled to use.
      if (signOut) {
        await signOut({ federated: false });
      }
      // Then navigate to login
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if signout fails, still try to navigate to login
      navigate('/login');
    }
  };

  if (showLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <WebsiteLogo width={240} className="mb-8" />
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kidsbooked-blue mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">{loadingMessage}</p>
          <p className="text-gray-500 text-sm mt-2">This may take a few moments...</p>
        </div>
      </div>
    );
  }

  // Show error state
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
      <WebsiteLogo width={240} className="mb-8" />
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Oops, something went wrong.</h1>
        <p className="text-gray-600 mb-6">
          Either your login expired, the page address is incorrect, or your account is not yet approved.
        </p>
        <Button 
          onClick={handleGoToLogin}
          className="bg-kidsbooked-blue text-white px-6 py-2 rounded-md hover:bg-blue-600 transition-colors mb-4"
        >
          Go to Login page
        </Button>
        <p className="text-gray-500 text-sm">
          If you need help, please contact support.
        </p>
      </div>
    </div>
  );
};

export default RoleProtectedRoute;
